import gql from 'graphql-tag';

/**
 * The venue resource is fetched in stages so the loader can report progress.
 * Each query warms the Apollo cache; `completeQuery.ts` then reads the whole
 * thing back in one go with readFragment - which returns null if any field it
 * asks for is missing, so every `image` here must include `file { id url }`.
 */

const ITEM_FRAGMENT = gql`
  fragment itemInEpos on Item {
    id
    ageRestricted
    order
    defaultPrice
    titleLang {
      id
      se
      en
      es
      no
    }
    vatRate
    descriptionLang {
      id
      se
      en
      es
      no
    }
    image {
      id
      file {
        id
        url
      }
    }

    upsalesSortOrder
    upsales {
      id
      hideInEPOS
      enableMultiSelect
      itemsSortingOrder
      icon
      name {
        id
        se
        en
        es
        no
      }
      items {
        id
      }
    }

    categories {
      id
    }

    modificationGroups
    allergyTags
  }
`;

const VARIATION_FRAGMENT = gql`
  fragment variationInEpos on Variation {
    id
    name
    nameLang {
      id
      se
      en
      es
      no
    }
    asItem {
      id
    }
    price
    image {
      id
      file {
        id
        url
      }
    }
  }
`;

export const EPOS_VARIATIONS_QUERY = gql`
  query EposVariations($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        variations {
          ...variationInEpos
        }
      }
    }
  }

  ${VARIATION_FRAGMENT}
`;

/**
 * Pulls every modification group into the Apollo cache. Items only carry the
 * group ids - useItemModifications reads the groups back by id via
 * readFragment, so the field set here must cover what that fragment asks for.
 */
export const EPOS_MODIFICATION_GROUPS_QUERY = gql`
  query EposModificationGroups($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        modificationGroups {
          id
          internalName
          externalName
          type
          required
          expandedByDefault
          preselectedVariations
          isGridViewEnabled
          icon
          variations {
            id
            price
            name
            nameLang {
              id
              se
              en
              es
              no
            }
            asItem {
              id
            }
            image {
              id
              file {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`;

export const EPOS_ITEMS_QUERY = gql`
  query EposItems($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        items {
          ...itemInEpos
        }
      }
    }
  }

  ${ITEM_FRAGMENT}
`;

export const EPOS_VENUE_QUERY = gql`
  query EposVenue($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      availabilities {
        id
        dayOfWeek
        timeRange {
          id
          start
          end
        }
      }
      image {
        id
        file {
          id
          url
        }
      }
      company {
        id
        defaultOrderStatus
        offers {
          id
          price
          type
          discountType
          discountRate
          overrideItemPrices
          nameLang {
            id
            se
            en
            es
            no
          }
          descriptionLang {
            id
            se
            en
            es
            no
          }
          image {
            id
            file {
              id
              url
            }
          }
          items {
            id
            titleLang {
              id
              se
              en
              es
              no
            }
          }
          stages {
            id
            discountRate
            discountType
            title {
              id
              se
              en
              es
              no
            }
            items {
              id
            }
          }
        }
      }
    }
  }
`;

export const EPOS_CATEGORIES_QUERY = gql`
  query EposCategories($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        categories {
          id
          order
          color
          deleted
          hideInCustomerOrderingFlows
          ukVatRate
          name
          icon

          nameLang {
            id
            se
            en
            es
            no
          }
          descriptionLang {
            id
            se
            en
            es
            no
          }

          items(filter: { deleted: false }) {
            id
          }

          image {
            id

            file {
              id

              url
            }
          }
        }
      }
    }
  }
`;
